import { type WidgetType } from 'src/components/card-widget/types';
import { COUNTRY_NAME_BY_CURRENCY_MAP } from 'src/constants/country-name-by-currency-map';
import { INSTALLMENT_PLANS } from 'src/constants/learn-more';
import { SUBVENTION_COUNTRY } from 'src/constants/subvention-country';
import { findExperiment } from 'src/utils/find-experiment';
import { getLongestSubventionPlan } from 'src/utils/get-longest-subvention-plan';

import { deriveProductFacts } from './product-facts';
import { type LearnMoreDecision, type WidgetDecisionInput } from './types';

export type CardDecision = Readonly<{
  surface: 'card';
  widgetType: WidgetType;
  learnMore: LearnMoreDecision;
}>;

/**
 * Preserves the observed mixed-product priority. Product confirmation of this
 * ordering is pending; changing it must be a separate behavior decision.
 */
export const decideCard = (input: WidgetDecisionInput): CardDecision => {
  const facts = deriveProductFacts(input);
  const isPi4 =
    facts.hasInstallments &&
    input.installmentsCount === INSTALLMENT_PLANS.four &&
    facts.installments.some(
      (installment) => installment.paymentsCount === INSTALLMENT_PLANS.four
    );
  const isSubvention =
    SUBVENTION_COUNTRY.includes(COUNTRY_NAME_BY_CURRENCY_MAP[input.currency]) &&
    Boolean(getLongestSubventionPlan(facts.installments, input.currency));
  const isLtfKsaSnippetExp =
    findExperiment('BNPL-11864-KSA-LTF-snippet', input.experiments) === 'test';
  const hasInstallmentAmount = facts.installments.some(
    (installment) => installment.amount > 0
  );

  let widgetType: WidgetType;

  if (isSubvention) {
    widgetType = 'subvention';
  } else if (
    isLtfKsaSnippetExp &&
    hasInstallmentAmount &&
    facts.shouldShowLongTermContent &&
    COUNTRY_NAME_BY_CURRENCY_MAP[input.currency] === 'saudi'
  ) {
    widgetType = 'ltf-ksa';
  } else if (isPi4) {
    widgetType = facts.shouldShowLongTermContent ? 'pi4-ltf' : 'pi4';
  } else if (facts.hasValidMonthlyBillingDate) {
    widgetType = 'pay-later';
  } else if (facts.payInFullAvailable) {
    widgetType = 'pay-in-full';
  } else {
    widgetType = 'generic';
  }

  const learnMoreByWidget: Record<WidgetType, LearnMoreDecision> = {
    subvention: facts.shouldOpenLongTermLearnMore ? 'ltf' : 'default',
    'pi4-ltf': 'ltf',
    pi4: facts.shouldOpenLongTermLearnMore ? 'ltf' : 'pix',
    'pay-later': 'pix',
    'pay-in-full': 'pif',
    generic: facts.shouldOpenLongTermLearnMore ? 'ltf' : 'default',
    'ltf-ksa': 'ltf',
  };

  return {
    surface: 'card',
    widgetType,
    learnMore: learnMoreByWidget[widgetType],
  };
};
